/**
 * Modal de Configuração de Widgets do Dashboard
 * 
 * @author Equipe GestaoeCRM
 * @date 19/12/2025
 */

import { useState } from 'react';
import { X, Eye, EyeOff, GripVertical, RotateCcw } from 'lucide-react';
import { useDashboardWidgets, WidgetId } from '@/hooks/useDashboardWidgets';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';

interface DashboardWidgetConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DashboardWidgetConfigModal({
  isOpen,
  onClose,
}: DashboardWidgetConfigModalProps) {
  const { widgets, toggleWidget, resetWidgets } = useDashboardWidgets();

  if (!isOpen) return null;

  const handleToggle = (widgetId: WidgetId) => {
    toggleWidget(widgetId);
    toast.success('Configuração salva');
  };

  const handleReset = () => {
    if (confirm('Deseja restaurar a configuração padrão dos widgets?')) {
      resetWidgets();
      toast.success('Configuração restaurada');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-background-glass backdrop-blur-glass border border-border-glass rounded-glass shadow-glass max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-glass">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">
              Personalizar Dashboard
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Configure quais widgets aparecem no seu dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-button hover:bg-background-glass-hover transition-colors duration-200"
          >
            <X className="h-5 w-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-4">
          <div className="space-y-2">
            {widgets.map((widget) => (
              <div
                key={widget.id}
                className={cn(
                  'flex items-center justify-between p-3 rounded-button border border-border-glass bg-background-glass-hover',
                  'hover:border-secondary/30 transition-all duration-200'
                )}
              >
                <div className="flex items-center space-x-3 flex-1">
                  <GripVertical className="h-5 w-5 text-text-muted cursor-move" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-text-primary">
                      {widget.name}
                    </h3>
                    <p className="text-xs text-text-secondary mt-1">
                      {widget.description}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle(widget.id)}
                  className={cn(
                    'p-2 rounded-button transition-colors duration-200',
                    widget.visible
                      ? 'text-green-400 hover:bg-green-500/10'
                      : 'text-text-muted hover:bg-background-glass'
                  )}
                  title={widget.visible ? 'Ocultar widget' : 'Mostrar widget'}
                >
                  {widget.visible ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border-glass">
          <button
            onClick={handleReset}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary rounded-button hover:bg-background-glass-hover transition-colors duration-200"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Restaurar Padrão</span>
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-hover transition-colors duration-200"
          >
            Concluído
          </button>
        </div>
      </div>
    </div>
  );
}

